import { useEffect, useMemo, useRef, useState } from 'react';
import '../datePickerStyles.css';
import { addYears, subYears } from 'date-fns';
import {
  SessionStorageItems,
  SessionStorageService,
} from '../../../services/sessionStorageService';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { Skeleton } from '@mui/material';
import { useAppDispatch } from '../../../../../app/store';
import { summaryApi } from '../../../api/summaryApi';
import type { ITrackedTime } from '../../../models/IEmployeeTrackedTime';
import { getMonthRanges } from '../../../utils/dateUtils';
import {
  findMonthTrackedTime,
  formatTrackedTimePreviewLine,
  parseTrackedTimeDates,
  pickTrackedTimeBucket,
} from '../../../utils/summaryDisplayUtils';

type MonthPickerProps = {
  onChange?: (month: Date) => void;
  disabled?: boolean;
  useLocalTime: boolean;
  clientId?: number;
  showTrackedTimePreview?: boolean;
  currencySymbol?: string;
  currencyCode?: string;
};

const shortMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const MonthPicker = ({
  onChange,
  disabled,
  useLocalTime,
  clientId,
  showTrackedTimePreview = false,
  currencySymbol = '',
  currencyCode,
}: MonthPickerProps) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const initialMonth = useMemo(
    () =>
      SessionStorageService.getDatePickerLastDateRangeOrDefault(
        SessionStorageItems.monthPickerLastDateRange
      ).firstDay,
    []
  );
  const [date, setDate] = useState(initialMonth);
  const [selectedDate, setSelectedDate] = useState(initialMonth);
  const [data, setData] = useState<ITrackedTime[]>();
  const [navigationLoading, setNavigationLoading] = useState(false);
  const [fetchedYear, setFetchedYear] = useState<number | null>(null);

  const countActiveRequest = useRef(0);
  const displayedYear = date.getFullYear();

  const needsFetch = showTrackedTimePreview && !!currencyCode && fetchedYear !== displayedYear;
  const isLoading =
    showTrackedTimePreview && (navigationLoading || (needsFetch && data === undefined));

  useEffect(() => {
    if (!needsFetch) {
      return;
    }

    const requestId = ++countActiveRequest.current;
    let cancelled = false;
    const year = displayedYear;
    const { monthRanges } = getMonthRanges(date);

    dispatch(
      summaryApi.endpoints.getTrackedTimePerMonth.initiate({
        dateRanges: monthRanges,
        useLocalTime,
        clientId,
      })
    )
      .unwrap()
      .then((result) => {
        if (cancelled || requestId !== countActiveRequest.current) {
          return;
        }

        setData(parseTrackedTimeDates(pickTrackedTimeBucket(result, currencyCode)));
        setFetchedYear(year);
      })
      .catch(() => {
        // Preview falls back to zero values when the request fails.
      })
      .finally(() => {
        if (!cancelled && requestId === countActiveRequest.current) {
          setNavigationLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, currencyCode, date, dispatch, displayedYear, needsFetch, useLocalTime]);

  const handleYearChange = (next: boolean) => {
    if (showTrackedTimePreview) {
      countActiveRequest.current++;
      setNavigationLoading(true);
    }

    setDate(next ? addYears(date, 1) : subYears(date, 1));
  };

  const onMonthClick = (month: Date) => {
    setSelectedDate(month);
    SessionStorageService.setDatePickerLastDateRange(
      month,
      new Date(month.getFullYear(), month.getMonth() + 1, 0),
      SessionStorageItems.monthPickerLastDateRange
    );
    if (onChange) {
      onChange(month);
    }
    setOpen(false);
  };

  const getFormattedCalendarHeaderMonthPicker = (d: Date): string => `${d.getFullYear()}`;

  const getFormattedDateMonthPicker = (d: Date): string => {
    return `${d.toLocaleString('en-US', { month: 'long' })} ${d.getFullYear()}`;
  };

  const getMonthClassName = (month: Date): string => {
    const isCurrentMonth =
      month.getMonth() === selectedDate.getMonth() &&
      month.getFullYear() === selectedDate.getFullYear();
    return isCurrentMonth ? 'calendar-cell selected-data' : 'calendar-cell state-hover';
  };

  const renderMonthSubtext = (monthDate: Date) => {
    if (isLoading) {
      return <Skeleton variant="rectangular" width={90} height={15} />;
    }

    const match = findMonthTrackedTime(data, monthDate);
    return formatTrackedTimePreviewLine(match?.Price, match?.TotalHours, currencySymbol);
  };

  const renderMonths = () => {
    return (
      <>
        {[0, 1, 2].map((row) => (
          <tr key={row}>
            {shortMonths.slice(row * 4, row * 4 + 4).map((monthName, index) => {
              const monthDate = new Date(date.getFullYear(), row * 4 + index, 1);
              return (
                <td
                  key={index}
                  className={getMonthClassName(monthDate)}
                  onClick={() => onMonthClick(monthDate)}
                >
                  {showTrackedTimePreview ? (
                    <div className="month-cell">
                      <span>{monthName}</span>
                      <div className="month-subtext">{renderMonthSubtext(monthDate)}</div>
                    </div>
                  ) : (
                    monthName
                  )}
                </td>
              );
            })}
          </tr>
        ))}
      </>
    );
  };

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <>
      <div
        className="picker-mask"
        style={{ display: open ? 'block' : 'none' }}
        onClick={() => setOpen(false)}
      />
      <div className="date-picker-toolbar">
        <a
          id="prevOuterButton"
          className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-previous-view datepicker-calendar-navigator-previous-month custom-date-picker-calendar-left"
          style={{ border: 'none' }}
          onClick={() =>
            !disabled &&
            onMonthClick(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1))
          }
        >
          <div className="datepicker-button-content">
            <ArrowBackIosNewIcon className="datepicker-icon" fontSize="inherit" />
          </div>
        </a>
        <div className="custom-date-picker-container">
          <div className="custom-date-picker-container-text-container">
            <input
              className="custom-date-picker-container-text"
              type="text"
              value={getFormattedDateMonthPicker(selectedDate)}
              readOnly
            />
          </div>
          <div className="custom-date-picker-container-button">
            <div className="calendar-icon-container" onClick={() => !disabled && setOpen(!open)}>
              <CalendarMonthOutlinedIcon className="calendar-icon" />
            </div>
          </div>
          {open && (
            <div
              ref={popupRef}
              className="datepicker-overlay-content mobile-date-picker datepicker-popup-normal datepicker-resizable datepicker-popup-inherit-height custom-date-picker-content"
            >
              <div className="datepicker-popup-content custom-date-picker-popup-content">
                <div className="custom-date-picker-content-calendar">
                  <div className="datepicker-widget datepicker-calendar-navigator">
                    <a
                      id="prev"
                      className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-previous-view datepicker-calendar-navigator-previous-month custom-date-picker-calendar-left"
                      onClick={() => handleYearChange(false)}
                    >
                      <div className="datepicker-button-content">
                        <ArrowBackIosNewIcon className="datepicker-icon" fontSize="inherit" />
                      </div>
                    </a>
                    <a className="datepicker-calendar-caption-button datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-text week-calendar-center">
                      <div className="datepicker-button-content">
                        <span className="datepicker-button-text">
                          {getFormattedCalendarHeaderMonthPicker(date)}
                        </span>
                      </div>
                    </a>
                    <a
                      id="next"
                      className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-next-view datepicker-calendar-navigator-next-month custom-date-picker-calendar-right"
                      onClick={() => handleYearChange(true)}
                    >
                      <div className="datepicker-button-content">
                        <ArrowForwardIosIcon className="datepicker-icon" fontSize="inherit" />
                      </div>
                    </a>
                  </div>
                  <div className="calendar-body">
                    <div className="datepicker-calendar-views-wrapper">
                      <div className="datepicker-widget calendar-table">
                        <table>
                          <tbody>{renderMonths()}</tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <a
          id="nextOuterButton"
          className="datepicker-button datepicker-button-normal datepicker-button-mode-contained datepicker-widget datepicker-button-has-icon datepicker-calendar-navigator-next-view datepicker-calendar-navigator-next-month custom-date-picker-calendar-right"
          style={{ border: 'none' }}
          onClick={() =>
            !disabled &&
            onMonthClick(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1))
          }
        >
          <div className="datepicker-button-content">
            <ArrowForwardIosIcon className="datepicker-icon" fontSize="inherit" />
          </div>
        </a>
      </div>
    </>
  );
};
